import { z } from "zod";

const sha256Schema = z.string().regex(/^[0-9a-f]{64}$/u);
const artworkIdSchema = z.string().regex(/^[0-9a-f]{32}$/u);
const slugSchema = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/u).max(64);
const languageSchema = z.string().regex(/^[a-z]{2}(?:-[a-z0-9]+)*$/u);
const safeFilenameSchema = z
  .string()
  .min(1)
  .refine((value) => !value.includes("/") && !value.includes("\\") && value !== "..", {
    message: "asset path must be one safe relative filename",
  });

const artifactSchema = z
  .object({
    path: safeFilenameSchema,
    size: z.number().int().positive(),
    sha256: sha256Schema,
  })
  .strict();

const publicArtifactSchema = artifactSchema.extend({
  path: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*\/[A-Za-z0-9._-]+$/u),
});

export const exhibitionManifestSchema = z
  .object({
    schema_version: z.literal("1.0"),
    exhibition_slug: slugSchema,
    plan_version: z.number().int().min(1).max(9999),
    title: z.string().trim().min(1),
    thesis: z.string().trim().min(1),
    room_ids: z.array(slugSchema).min(1).max(2),
    artwork_ids: z.array(artworkIdSchema).min(1).max(24),
    semantic_scene_sha256: sha256Schema,
    scene: artifactSchema.extend({ path: z.literal("museum.glb") }).strict(),
    content: artifactSchema.extend({ path: z.literal("museum-content.json") }).strict(),
    report: artifactSchema.extend({ path: z.literal("complete-build-report.json") }).strict(),
    cover: artifactSchema.extend({ path: z.string().regex(/^cover\.(?:png|jpe?g|webp)$/u) }).strict(),
    localizations: z.record(languageSchema, artifactSchema).default({}),
  })
  .strict()
  .superRefine((manifest, context) => {
    if (new Set(manifest.room_ids).size !== manifest.room_ids.length) {
      context.addIssue({ code: "custom", message: "manifest room IDs must be unique" });
    }
    if (new Set(manifest.artwork_ids).size !== manifest.artwork_ids.length) {
      context.addIssue({ code: "custom", message: "manifest artwork IDs must be unique" });
    }
    for (const [language, descriptor] of Object.entries(manifest.localizations)) {
      if (descriptor.path !== `museum-content.${language}.json`) {
        context.addIssue({ code: "custom", message: "localization path differs from language" });
      }
    }
  });

const menuLocalizationSchema = z
  .object({
    title: z.string().trim().min(1),
    summary: z.string().trim().min(1).max(280),
  })
  .strict();

const publicExhibitionSchema = z
  .object({
    slug: slugSchema,
    title: z.string().trim().min(1),
    summary: z.string().trim().min(1).max(280),
    room_count: z.number().int().min(1).max(2),
    artwork_count: z.number().int().min(1).max(24),
    manifest: publicArtifactSchema,
    cover: publicArtifactSchema,
    localizations: z.record(languageSchema, menuLocalizationSchema).default({}),
  })
  .strict()
  .superRefine((exhibition, context) => {
    if (
      !exhibition.manifest.path.startsWith(`${exhibition.slug}/`) ||
      !exhibition.cover.path.startsWith(`${exhibition.slug}/`)
    ) {
      context.addIssue({ code: "custom", message: "registry paths must be scoped by slug" });
    }
  });

export const exhibitionRegistrySchema = z
  .object({
    schema_version: z.literal("1.0"),
    exhibitions: z.array(publicExhibitionSchema),
  })
  .strict()
  .superRefine((registry, context) => {
    const slugs = registry.exhibitions.map((exhibition) => exhibition.slug);
    if (new Set(slugs).size !== slugs.length) {
      context.addIssue({ code: "custom", message: "registry slugs must be unique" });
    }
  });

const contentRouteSchema = z
  .object({
    order: z.number().int().min(1).max(24),
    room_id: slugSchema,
    artwork_id: artworkIdSchema,
    object_name: z.string().regex(/^artwork_[0-9a-f]{32}$/u),
  })
  .strict();

const displayImageSchema = z
  .object({
    path: z.string().regex(/^textures\/artwork_[0-9a-f]{32}\.[a-z0-9]+$/u),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    aspect_ratio: z.number().positive(),
    mean_luminance: z.number().min(0).max(255),
  })
  .strict();

const contentArtworkSchema = z
  .object({
    id: artworkIdSchema,
    object_name: z.string().regex(/^artwork_[0-9a-f]{32}$/u),
    route_order: z.number().int().min(1).max(24),
    room_id: slugSchema,
    title: z.string().trim().min(1),
    author: z.string().trim().min(1),
    century: z.string().trim().min(1),
    styles: z.array(z.string()),
    deities: z.array(z.string()),
    mortals: z.array(z.string()),
    museum: z.string().nullable(),
    review_markdown: z.string().trim().min(1),
    display_image: displayImageSchema,
  })
  .strict();

export const museumContentSchema = z
  .object({
    schema_version: z.literal("2.0"),
    exhibition_slug: slugSchema,
    plan_version: z.number().int().min(1).max(9999),
    title: z.string().trim().min(1),
    thesis: z.string().trim().min(1),
    entrance_room_id: slugSchema,
    rooms: z
      .array(z.object({ id: slugSchema, title: z.string().trim().min(1) }).strict())
      .min(1)
      .max(2),
    connection_id: slugSchema.nullable(),
    route: z.array(contentRouteSchema).min(1).max(24),
    artworks: z.array(contentArtworkSchema).min(1).max(24),
  })
  .strict()
  .superRefine((content, context) => {
    const roomIds = content.rooms.map((room) => room.id);
    if (
      new Set(roomIds).size !== roomIds.length ||
      !roomIds.includes(content.entrance_room_id) ||
      ((content.rooms.length === 1) !== (content.connection_id === null))
    ) {
      context.addIssue({ code: "custom", message: "content room topology differs" });
    }
    const routeIds = content.route.map((stop) => stop.artwork_id);
    const artworkIds = content.artworks.map((artwork) => artwork.id);
    if (
      JSON.stringify(artworkIds) !== JSON.stringify(routeIds) ||
      new Set(routeIds).size !== routeIds.length
    ) {
      context.addIssue({ code: "custom", message: "content route differs from artworks" });
    }
    content.route.forEach((stop, index) => {
      const artwork = content.artworks[index];
      if (
        artwork === undefined ||
        stop.order !== index + 1 ||
        artwork.route_order !== stop.order ||
        !roomIds.includes(stop.room_id) ||
        stop.object_name !== `artwork_${stop.artwork_id}` ||
        artwork.object_name !== stop.object_name ||
        artwork.room_id !== stop.room_id
      ) {
        context.addIssue({ code: "custom", message: `route identity differs at order ${index + 1}` });
      }
    });
  });

const reportArtifactSchema = artifactSchema.extend({
  path: z.enum([
    "museum.blend",
    "museum.glb",
    "museum-content.json",
    "museum-plan.v1.json",
    "preview.png",
    "preview-room2.png",
    "preview-final.png",
  ]),
});

export const completeBuildReportSchema = z
  .object({
    schema_version: z.literal("2.0"),
    exhibition_slug: slugSchema,
    status: z.literal("success"),
    blender_version: z.string().min(1),
    room_count: z.number().int().min(1).max(2),
    connection_count: z.number().int().min(0).max(1),
    artwork_ids: z.array(artworkIdSchema).min(1).max(24),
    object_counts: z
      .object({
        room_collections: z.number().int().min(1).max(2),
        room_floors: z.number().int().min(1).max(2),
        wall_segments: z.number().int().min(4).max(12),
        corridor_segments: z.number().int().min(0).max(4),
        paintings: z.number().int().min(1).max(24),
        frames: z.number().int().min(1).max(24),
        label_anchors: z.number().int().min(1).max(24),
        route_stops: z.number().int().min(1).max(24),
        painting_lights: z.number().int().min(1).max(24),
        room_fills: z.number().int().min(1).max(2),
        visitor_spawns: z.literal(1),
        exported_cameras: z.literal(1),
      })
      .strict(),
    source_hashes: z
      .object({
        plan: sha256Schema,
        catalog: sha256Schema,
        build_spec: sha256Schema,
        content: sha256Schema,
        staged_plan: sha256Schema,
      })
      .strict(),
    semantic_scene_sha256: sha256Schema,
    artifacts: z.array(reportArtifactSchema).min(6).max(7),
    warnings: z.array(z.string()),
    errors: z.array(z.string()).length(0),
  })
  .strict();

const localizedRoomSchema = z
  .object({ id: slugSchema, title: z.string().trim().min(1) })
  .strict();
const localizedArtworkSchema = z
  .object({
    id: artworkIdSchema,
    title: z.string().trim().min(1),
    author: z.string().trim().min(1),
    styles: z.array(z.string()),
    deities: z.array(z.string()),
    mortals: z.array(z.string()),
    museum: z.string().nullable(),
    review_markdown: z.string().trim().min(1),
  })
  .strict();

export const exhibitionLocalizationSchema = z
  .object({
    schema_version: z.literal("1.0"),
    exhibition_slug: slugSchema,
    language: languageSchema,
    title: z.string().trim().min(1),
    thesis: z.string().trim().min(1),
    rooms: z.array(localizedRoomSchema).min(1).max(2),
    artworks: z.array(localizedArtworkSchema).min(1).max(24),
  })
  .strict();

export type ExhibitionRegistry = z.infer<typeof exhibitionRegistrySchema>;
export type PublicExhibition = ExhibitionRegistry["exhibitions"][number];
export type ExhibitionManifest = z.infer<typeof exhibitionManifestSchema>;
export type MuseumContent = z.infer<typeof museumContentSchema>;
export type ContentArtwork = MuseumContent["artworks"][number];
export type CompleteBuildReport = z.infer<typeof completeBuildReportSchema>;
export type ExhibitionLocalization = z.infer<typeof exhibitionLocalizationSchema>;

// Transitional aliases keep lower-level modules small while the public handoff is now per exhibition.
export const viewerAssetsSchema = exhibitionManifestSchema;
export type ViewerAssets = ExhibitionManifest;
